import { useEffect, useMemo, useState } from "react";
import {
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Button,
  Dropdown,
  Option,
  Combobox,
  Field,
  Input,
  Switch,
  Radio,
  RadioGroup,
  Textarea,
  Body1,
  Caption1,
  makeStyles,
  tokens
} from "@fluentui/react-components";

export interface LeaveTypeOption {
  id: string;
  name: string;
  approvalType: "static_manager" | "auto_approved";
  countsAgainstBalance: boolean;
  unlimited: boolean;
  includeWeekends: boolean;
  customFieldName: string | null;
  customFieldRequired: boolean | null;
  isActive: boolean;
}

interface EligibleUser {
  id: string;
  displayName: string;
  email: string;
  departmentName: string | null;
  managerName: string | null;
  /** "report" (manager scope) | "delegated" (substitute scope) | "admin" */
  source: "report" | "delegated" | "admin";
}

interface Props {
  open: boolean;
  leaveTypes: LeaveTypeOption[];
  onClose: () => void;
  onCreated: () => void;
  defaultStartDate?: string;
}

const useStyles = makeStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM
  },
  dateRow: {
    display: "flex",
    gap: tokens.spacingHorizontalM
  },
  dateCell: { flex: 1 },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXL,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`
  },
  error: { color: tokens.colorPaletteRedForeground1 },
  hint: { color: tokens.colorNeutralForeground3 },
  onBehalfHint: {
    color: tokens.colorNeutralForeground3,
    display: "block",
    marginTop: tokens.spacingVerticalXXS
  }
});

const SELF_OPTION_VALUE = "__self";

// Local-time today as YYYY-MM-DD. `new Date().toISOString()` is UTC and would
// pre-fill tomorrow's date in the evening for users west of UTC.
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function NewRequestDrawer({ open, leaveTypes, onClose, onCreated, defaultStartDate }: Props) {
  const styles = useStyles();
  const initialDate = defaultStartDate ?? today();
  const [leaveTypeId, setLeaveTypeId] = useState<string>("");
  const [startDate, setStartDate] = useState<string>(initialDate);
  const [endDate, setEndDate] = useState<string>(initialDate);
  const [halfDay, setHalfDay] = useState<boolean>(false);
  const [amPm, setAmPm] = useState<"AM" | "PM">("AM");
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Phase 4.11 — "submit on behalf of". Empty = self. Lazily fetched on first
  // open; if the list is empty (plain employee, no delegations) the picker is
  // hidden entirely and the drawer behaves exactly like pre-4.11.
  const [eligibleUsers, setEligibleUsers] = useState<EligibleUser[] | null>(null);
  const [onBehalfOfUserId, setOnBehalfOfUserId] = useState<string>("");

  const selectedType = useMemo(
    () => leaveTypes.find((t) => t.id === leaveTypeId) ?? null,
    [leaveTypes, leaveTypeId]
  );

  const onBehalfOfUser = useMemo(
    () => (eligibleUsers ?? []).find((u) => u.id === onBehalfOfUserId) ?? null,
    [eligibleUsers, onBehalfOfUserId]
  );

  useEffect(() => {
    // Reset form whenever the drawer opens.
    if (open) {
      const d = defaultStartDate ?? today();
      setLeaveTypeId("");
      setStartDate(d);
      setEndDate(d);
      setHalfDay(false);
      setAmPm("AM");
      setNotes("");
      setOnBehalfOfUserId("");
      setError(null);
      setSubmitting(false);
    }
  }, [open, defaultStartDate]);

  useEffect(() => {
    // Fetch the on-behalf-of options the first time the drawer opens in this
    // mount. We keep the cache across reopens to avoid hitting the API every
    // time the user clicks "New request"; the list rarely changes mid-session.
    if (open && eligibleUsers === null) {
      let cancelled = false;
      (async () => {
        try {
          const r = await fetch("/api/eligible-requesters");
          if (!r.ok) {
            // 401/403 just means we can't offer the picker — fail soft.
            if (!cancelled) setEligibleUsers([]);
            return;
          }
          const body = (await r.json()) as { users: EligibleUser[] };
          if (!cancelled) setEligibleUsers(body.users ?? []);
        } catch {
          if (!cancelled) setEligibleUsers([]);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
  }, [open, eligibleUsers]);

  useEffect(() => {
    // When toggling half-day on, force the end date to match start date.
    if (halfDay && endDate !== startDate) setEndDate(startDate);
  }, [halfDay, startDate, endDate]);

  // Legacy per-type "this field is required" flag now gates the unified
  // Reason / Notes field instead of the dropped custom_field_1 input.
  const notesRequired = !!selectedType?.customFieldRequired;
  const canSubmit =
    !!leaveTypeId &&
    !!startDate &&
    !!endDate &&
    endDate >= startDate &&
    (!notesRequired || notes.trim().length > 0) &&
    !submitting;

  const showOnBehalfPicker = (eligibleUsers?.length ?? 0) > 0;

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch("/api/leave-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          leaveTypeId,
          startDate,
          endDate,
          halfDay,
          amPm: halfDay ? amPm : null,
          notes: notes.trim() || null,
          onBehalfOfUserId: onBehalfOfUserId || null
        })
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${r.status}`);
      }
      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <OverlayDrawer open={open} onOpenChange={(_, d) => !d.open && onClose()} position="end" size="medium">
      <DrawerHeader>
        <DrawerHeaderTitle>New leave request</DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.body}>
          {showOnBehalfPicker && (
            <Field label="Submit for">
              <Combobox
                placeholder="Myself"
                value={onBehalfOfUser?.displayName ?? "Myself"}
                selectedOptions={[onBehalfOfUserId || SELF_OPTION_VALUE]}
                onOptionSelect={(_, d) => {
                  const v = String(d.optionValue ?? "");
                  setOnBehalfOfUserId(v === SELF_OPTION_VALUE ? "" : v);
                }}
              >
                <Option value={SELF_OPTION_VALUE} text="Myself">
                  Myself
                </Option>
                {(eligibleUsers ?? []).map((u) => (
                  <Option key={u.id} value={u.id} text={u.displayName}>
                    {u.displayName}
                    {u.departmentName ? ` — ${u.departmentName}` : ""}
                    {u.source === "delegated" ? " (via delegation)" : ""}
                  </Option>
                ))}
              </Combobox>
              {onBehalfOfUser && (
                <Caption1 className={styles.onBehalfHint}>
                  Filing on behalf of <strong>{onBehalfOfUser.displayName}</strong>
                  {onBehalfOfUser.managerName ? ` — manager: ${onBehalfOfUser.managerName}` : ""}.
                  Their manager will receive the approval request; they'll get an email confirming you filed it.
                </Caption1>
              )}
            </Field>
          )}

          <Field label="Leave type" required>
            <Dropdown
              placeholder="Select a leave type"
              selectedOptions={leaveTypeId ? [leaveTypeId] : []}
              value={selectedType?.name ?? ""}
              onOptionSelect={(_, d) => setLeaveTypeId(String(d.optionValue ?? ""))}
            >
              {leaveTypes.map((t) => (
                <Option key={t.id} value={t.id} text={t.name}>
                  {t.name}
                  {t.approvalType === "auto_approved" ? " (auto-approved)" : ""}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Half day">
            <Switch checked={halfDay} onChange={(_, d) => setHalfDay(d.checked)} />
          </Field>

          {halfDay && (
            <Field label="Which half" required>
              <RadioGroup layout="horizontal" value={amPm} onChange={(_, d) => setAmPm(d.value === "PM" ? "PM" : "AM")}>
                <Radio value="AM" label="Morning (AM)" />
                <Radio value="PM" label="Afternoon (PM)" />
              </RadioGroup>
            </Field>
          )}

          <div className={styles.dateRow}>
            <div className={styles.dateCell}>
              <Field label="Start date" required>
                <Input type="date" value={startDate} onChange={(_, d) => setStartDate(d.value)} />
              </Field>
            </div>
            <div className={styles.dateCell}>
              <Field label="End date" required>
                <Input type="date" value={endDate} onChange={(_, d) => setEndDate(d.value)} disabled={halfDay} />
              </Field>
            </div>
          </div>

          <Field label="Reason / Notes" required={notesRequired}>
            <Textarea value={notes} onChange={(_, d) => setNotes(d.value)} rows={4} />
          </Field>

          {selectedType && (
            <Body1 className={styles.hint}>
              {selectedType.approvalType === "auto_approved"
                ? "This leave type is auto-approved and will be marked approved on submit."
                : onBehalfOfUser
                  ? `This request will be sent to ${onBehalfOfUser.managerName ?? "their manager"} for approval.`
                  : "This request will be sent to your manager for approval."}
            </Body1>
          )}

          {error && (
            <Body1 as="p" className={styles.error}>
              {error}
            </Body1>
          )}

          <div className={styles.footer}>
            <Button appearance="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={submit} disabled={!canSubmit}>
              {submitting ? "Submitting…" : "Submit request"}
            </Button>
          </div>
        </div>
      </DrawerBody>
    </OverlayDrawer>
  );
}
